import React, { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import Reveal from '@/components/motion/Reveal'
import EditableText from '@/components/editable/EditableText'
import EditableImage from '@/components/editable/EditableImage'

const POLES = [
  {
    n: '01',
    t: 'Narbonne',
    sub: 'Immersion & Camps',
    d: "Séjours immersifs, petits groupes, ateliers créatifs et expériences humaines en pleine nature. Un site dédié aux camps thématiques et aux retraites transformatrices, dans le sud de la France.",
    extra: "Chaque immersion est pensée comme une expérience complète : environnement, création, dynamique humaine, exploration personnelle et production concrète.",
    tags: ['Immersions', 'Camps', 'Ateliers créatifs', 'Nature'],
    img: 'https://static.lumi.new/92/92a13a5822937d46170972f336fa1e4c.png',
  },
  {
    n: '02',
    t: 'Gryon',
    sub: 'Laboratoire vivant',
    d: "Galerie, expositions, rencontres, laboratoire créatif et médias contemporains. Au cœur des Alpes suisses, un lieu inspirant qui rassemble artistes, créateurs et penseurs.",
    extra: "Le laboratoire favorise les croisements entre disciplines, générations et compétences afin de faire émerger des projets hybrides, innovants et profondément humains.",
    tags: ['Galerie', 'Expositions', 'Rencontres', 'Médias'],
    img: 'https://static.lumi.new/6c/6c405df9f2bfc5a7eada6380bceba297.png',
  },
  {
    n: '03',
    t: 'Laboratoire TI',
    sub: 'Médias & développement',
    d: "IA, outils numériques avancés, studio médias, innovation et création de contenus. Le pôle technologique du Centre, dédié à la production et à l'expérimentation des outils d'aujourd'hui.",
    extra: "Produire des contenus devient aussi une manière de transmettre, documenter, sensibiliser et créer des ponts entre les expériences vécues et le monde extérieur.",
    tags: ['IA', 'Studio', 'Innovation', 'Contenus'],
    img: 'https://static.lumi.new/0e/0ebe3e85a678b84cf7398616cb62c196.png',
  },
  {
    n: '04',
    t: 'Jeunes & Transmission',
    sub: 'Programmes adaptés',
    d: "Programmes pour les jeunes en transition ou en rupture : accompagnement, découverte des métiers modernes, créativité, numérique et expression artistique. Un pont vers l'insertion et l'autonomie.",
    extra: "Le Centre souhaite proposer des formats plus adaptés aux réalités actuelles : intelligence artificielle, création numérique, autonomie, expression et expérimentation concrète. L'objectif n'est pas uniquement d'apprendre — mais de permettre à chacun de retrouver une dynamique, une utilité et une capacité de projection.",
    tags: ['Accompagnement', 'IA', 'Insertion', 'Créativité'],
    img: 'https://static.lumi.new/4c/4cf2e859dd4f1bfb22891b1d33113713.png',
  },
]

interface PoleProps {
  pole: typeof POLES[number]
  index: number
}

const PoleArticle: React.FC<PoleProps> = ({ pole, index }) => {
  const ref = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] })
  const imgY = useTransform(scrollYProgress, [0, 1], ['-15%', '15%'])
  const imgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.1, 1, 1.1])

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className="grid lg:grid-cols-12 gap-0 bg-[#0E1B2C] group"
    >
      <div
        className={`lg:col-span-5 relative h-[280px] lg:h-[420px] overflow-hidden ${
          index % 2 === 1 ? 'lg:order-2' : ''
        }`}
      >
        <EditableImage
          contentKey={`poles.item${index}.img`}
          src={pole.img}
          alt={pole.t}
          section="Poles"
          label={`${pole.t} — Image`}
          className="absolute inset-0 w-full h-full object-cover opacity-65 group-hover:opacity-85 transition-opacity duration-[1200ms] ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E1B2C] via-[#0E1B2C]/30 to-transparent pointer-events-none" />

        <motion.div
          className="absolute top-6 left-6 text-[10px] tracking-luxe text-[#C9E26A]"
        >
          <EditableText contentKey={`poles.item${index}.sub`} section="Poles" label={`${pole.t} — Sous-titre`} as="span">
            {pole.sub.toUpperCase()}
          </EditableText>
        </motion.div>

        <motion.div
          className="absolute bottom-6 left-6 font-serif-display text-[28px] text-[#E8DCC4]"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="text-[#C8A24A] mr-2">·</span>{pole.t}
        </motion.div>

        <motion.div
          className="hidden"
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          {pole.sub.toUpperCase()}
        </motion.div>

        {/* Decorative corners */}
        <motion.div
          className="absolute top-4 right-4 w-8 h-8 border-t border-r border-[#C8A24A]/0 group-hover:border-[#C8A24A]/70 transition-colors duration-700"
        />
        <motion.div
          className="absolute bottom-4 left-4 w-8 h-8 border-b border-l border-[#C8A24A]/0 group-hover:border-[#C8A24A]/70 transition-colors duration-700"
        />
      </div>
      <div className="lg:col-span-7 p-8 lg:p-16 flex flex-col justify-center">
        <div className="flex items-baseline gap-6 mb-6">
          <div className="text-[10px] tracking-luxe text-[#C8A24A]">{pole.n}</div>
          <motion.div
            className="flex-1 h-px bg-[rgba(232,220,196,0.10)]"
            initial={{ scaleX: 0, originX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
        <motion.h4
          className="font-serif-display text-[34px] lg:text-[48px] leading-[1.05] text-[#E8DCC4] mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 }}
        >
          <EditableText contentKey={`poles.item${index}.t`} section="Poles" label={`${pole.t} — Titre`}>
            {pole.t}
          </EditableText>
        </motion.h4>
        <motion.p
          className="text-[15px] lg:text-[16px] leading-[1.75] text-[#C9BFA8] max-w-xl mb-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.4 }}
        >
          <EditableText contentKey={`poles.item${index}.d`} section="Poles" label={`${pole.t} — Description`} multiline as="span">
            {pole.d}
          </EditableText>
        </motion.p>
        <motion.p
          className="text-[14px] italic text-[#C8A24A]/90 border-l-2 border-[#C8A24A]/40 pl-4 max-w-xl mb-8"
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.5 }}
        >
          <EditableText contentKey={`poles.item${index}.extra`} section="Poles" label={`${pole.t} — Citation`} multiline as="span">
            {pole.extra}
          </EditableText>
        </motion.p>
        <motion.div
          className="flex flex-wrap gap-2"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{ visible: { transition: { staggerChildren: 0.06, delayChildren: 0.5 } } }}
        >
          {pole.tags.map((tag, ti) => (
            <motion.span
              key={tag}
              variants={{
                hidden: { opacity: 0, y: 10 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
              }}
              whileHover={{ scale: 1.05, borderColor: 'rgba(184,212,90,0.7)', color: '#E8DCC4' }}
              className="px-3 py-1.5 text-[10px] tracking-mid uppercase text-[#C9BFA8] border border-[rgba(232,220,196,0.12)] cursor-default"
            >
              <EditableText contentKey={`poles.item${index}.tag${ti}`} section="Poles" label={`${pole.t} — Tag ${ti + 1}`} as="span">{tag}</EditableText>
            </motion.span>
          ))}
        </motion.div>
      </div>
    </motion.article>
  )
}

const Poles: React.FC = () => {
  return (
    <section id="poles" className="relative bg-[#0E1B2C] py-32 lg:py-44 overflow-hidden">
      <motion.div
        className="absolute right-0 top-0 w-[700px] h-[700px] pointer-events-none blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(184,212,90,0.12), transparent 70%)' }}
        animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-24">
          <Reveal className="lg:col-span-4">
            <div className="text-[10px] tracking-luxe text-[#C8A24A] mb-4">
              <EditableText as="span" contentKey="poles.section.kicker" section="Poles" label="Numéro de section">03 — ÉCOSYSTÈME</EditableText>
            </div>
            <motion.div
              className="h-px bg-gradient-to-r from-[#C8A24A] to-transparent mb-6"
              initial={{ width: 0 }}
              whileInView={{ width: 96 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            />
            <div className="text-[12px] tracking-mid uppercase text-[#C9BFA8]">
              <EditableText as="span" contentKey="poles.kicker" section="Poles" label="Sous-titre">Quatre pôles complémentaires</EditableText>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-8" delay={0.15}>
            <h3 className="font-serif-display text-[36px] sm:text-[48px] lg:text-[60px] leading-[1.05] text-[#E8DCC4] text-balance">
              <EditableText contentKey="poles.title" section="Poles" label="Titre de section" multiline as="span">
                Une structure modulaire qui grandit, s'adapte et fait émerger les projets.
              </EditableText>
            </h3>
            <p className="mt-8 max-w-3xl text-[16px] leading-[1.7] text-[#C9BFA8]">
              <EditableText contentKey="poles.intro" section="Poles" label="Introduction" multiline as="span">
                Le Centre intègre progressivement plusieurs pôles complémentaires. Chacun apporte sa dimension propre — ensemble, ils composent un écosystème durable.
              </EditableText>
            </p>
          </Reveal>
        </div>

        <div className="space-y-px bg-[rgba(232,220,196,0.10)] hairline">
          {POLES.map((p, i) => (
            <PoleArticle key={p.n} pole={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  )
}

export default Poles
